import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Droplet, Activity, Skull, AlertTriangle, Eye,
  Plus, Trash2, BellRing, BellOff, ExternalLink, Loader2,
  Target, TrendingUp, XCircle,
  Volume2, VolumeX, Clock, ScrollText, X,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import {
  fetchWatchlist,
  addToWatchlist,
  removeFromWatchlist,
  patchWatchlist,
  snoozeAlerts,
  muteAlerts,
  fetchAlertLog,
} from "../lib/api";

// Poll the backend every 15s while the tab is visible. The backend itself
// polls DexScreener at age-tiered intervals; this is just how often the UI
// pulls aggregated state.
const REFRESH_MS = 15_000;

// Solana mint address shape (Base58, 32-44 chars).
const SOL_MINT_RE = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;

const TIER_META = {
  safe: {
    label: "Stable",
    variant: "success",
    icon: Droplet,
    badge: "🟢",
    rank: 0,
  },
  watch: {
    label: "Watch",
    variant: "warning",
    icon: Eye,
    badge: "🟡",
    rank: 1,
  },
  bleeding: {
    label: "Bleeding",
    variant: "warning",
    icon: Activity,
    badge: "🟠",
    rank: 2,
  },
  critical: {
    label: "Critical",
    variant: "danger",
    icon: AlertTriangle,
    badge: "🔴",
    rank: 3,
  },
  dead: {
    label: "Dead",
    variant: "danger",
    icon: Skull,
    badge: "⚫",
    rank: 4,
  },
};

function tierMeta(tier) {
  return TIER_META[tier] || TIER_META.safe;
}

function fmtUsd(n) {
  if (n == null || isNaN(n)) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}k`;
  return `$${n.toFixed(0)}`;
}

function fmtPct(n) {
  if (n == null || isNaN(n)) return "—";
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(1)}%`;
}

function fmtPrice(p) {
  if (p == null || isNaN(p)) return "—";
  if (p >= 1) return `$${p.toFixed(4)}`;
  if (p >= 0.001) return `$${p.toFixed(6)}`;
  return `$${p.toExponential(2)}`;
}

function fmtAge(seconds) {
  if (seconds == null || isNaN(seconds)) return "—";
  if (seconds < 60) return `${Math.floor(seconds)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}

function shortMint(mint) {
  if (!mint) return "";
  if (mint.length <= 10) return mint;
  return `${mint.slice(0, 4)}…${mint.slice(-4)}`;
}

/**
 * Tiny inline-SVG sparkline. Pure presentation, no deps.
 * `history` is an array of {ts, liq}.
 */
function Sparkline({ history, tier }) {
  if (!history || history.length < 2) {
    return (
      <div className="h-8 text-[10px] text-[hsl(var(--muted))] flex items-center">
        gathering data…
      </div>
    );
  }
  const vals = history.map((h) => h.liq);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = Math.max(max - min, 1);
  const W = 120;
  const H = 32;
  const stepX = W / (vals.length - 1);
  const pts = vals
    .map((v, i) => {
      const x = i * stepX;
      const y = H - ((v - min) / range) * H;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");

  const stroke =
    tier === "critical" || tier === "dead"
      ? "hsl(var(--danger))"
      : tier === "bleeding" || tier === "watch"
      ? "hsl(var(--warning))"
      : "hsl(var(--success))";

  return (
    <svg width={W} height={H} className="block" aria-hidden="true">
      <polyline
        fill="none"
        stroke={stroke}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={pts}
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Fibonacci entry-signal helpers
// ---------------------------------------------------------------------------

const FIB_SIGNAL_META = {
  entry: {
    label: "ENTRY",
    icon: Target,
    badge: "🎯",
    cls: "bg-[hsl(var(--success)/0.15)] text-[hsl(var(--success))] border-[hsl(var(--success)/0.5)] animate-pulse",
    rank: 100,
  },
  watching_zone: {
    label: "In zone",
    icon: Eye,
    badge: "👀",
    cls: "bg-[hsl(var(--warning)/0.12)] text-[hsl(var(--warning))] border-[hsl(var(--warning)/0.4)]",
    rank: 50,
  },
  invalidated: {
    label: "Invalidated",
    icon: XCircle,
    badge: "💀",
    cls: "bg-[hsl(var(--muted)/0.15)] text-[hsl(var(--muted))] border-[hsl(var(--border))]",
    rank: 10,
  },
  none: {
    label: "Tracking",
    icon: TrendingUp,
    badge: "📈",
    cls: "bg-[hsl(var(--bg-elev-3))] text-[hsl(var(--muted))] border-[hsl(var(--border))]",
    rank: 1,
  },
};

function fibSignalMeta(signal) {
  return FIB_SIGNAL_META[signal] || FIB_SIGNAL_META.none;
}

/**
 * Vertical Fib ladder. Renders swing-high at top, swing-low at bottom, with
 * 38.2 / 50 / 61.8 / 78.6 horizontal lines and a price dot at the latest
 * sample's position.
 */
function FibLadder({ fib }) {
  if (!fib || !fib.swing_low || !fib.swing_high) {
    return (
      <div className="h-16 text-[10px] text-[hsl(var(--muted))] flex items-center justify-center">
        gathering swing…
      </div>
    );
  }
  const low = fib.swing_low;
  const high = fib.swing_high;
  const range = Math.max(high - low, 1e-12);
  const samples = Array.isArray(fib.samples) ? fib.samples : [];
  const latest = samples.length > 0 ? samples[samples.length - 1] : null;
  const price = latest && typeof latest.price === "number" ? latest.price : null;

  const H = 64;
  const W = 90;
  // y=0 is top (high). We invert: high at top, low at bottom.
  const yFor = (val) => {
    const pct = (high - val) / range; // 0 at top (high), 1 at bottom (low)
    return Math.max(0, Math.min(H, pct * H));
  };

  const levels = fib.levels || {};
  const levelKeys = ["382", "500", "618", "786"];
  const levelLabels = { "382": "38.2", "500": "50", "618": "61.8", "786": "78.6" };
  const zoneTop = levels["500"];
  const zoneBot = levels["618"];

  return (
    <svg width={W} height={H} className="block" aria-label="Fib ladder">
      {/* Zone band 50–61.8 */}
      {zoneTop != null && zoneBot != null ? (
        <rect
          x="0"
          y={yFor(zoneTop)}
          width={W}
          height={Math.max(1, yFor(zoneBot) - yFor(zoneTop))}
          fill="hsl(var(--warning) / 0.12)"
        />
      ) : null}
      {/* Top (high) and bottom (low) bounds */}
      <line x1="0" y1="0.5" x2={W} y2="0.5" stroke="hsl(var(--success) / 0.5)" strokeWidth="1" />
      <line x1="0" y1={H - 0.5} x2={W} y2={H - 0.5} stroke="hsl(var(--danger) / 0.5)" strokeWidth="1" />
      {/* Fib levels */}
      {levelKeys.map((k) => {
        const v = levels[k];
        if (v == null) return null;
        const y = yFor(v);
        return (
          <g key={k}>
            <line
              x1="0"
              y1={y}
              x2={W}
              y2={y}
              stroke="hsl(var(--border))"
              strokeWidth="0.5"
              strokeDasharray="2 2"
            />
            <text
              x="2"
              y={y - 1}
              fontSize="7"
              fill="hsl(var(--muted))"
              fontFamily="monospace"
            >
              {levelLabels[k]}
            </text>
          </g>
        );
      })}
      {/* Current price dot */}
      {price != null ? (
        <circle
          cx={W - 6}
          cy={yFor(price)}
          r="3"
          fill="hsl(var(--accent, var(--success)))"
          stroke="hsl(var(--bg-elev-2))"
          strokeWidth="1"
        />
      ) : null}
    </svg>
  );
}

function FibSignalBadge({ fib }) {
  if (!fib) return null;
  const meta = fibSignalMeta(fib.signal);
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border ${meta.cls}`}
      title={fib.reason || ""}
    >
      <Icon size={10} />
      {meta.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Telegram alert state helpers
// ---------------------------------------------------------------------------

function fmtRelativeFuture(ts) {
  if (!ts) return null;
  const ms = ts * 1000 - Date.now();
  if (ms <= 0) return null;
  const mins = Math.round(ms / 60_000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.round(hours / 24)}d`;
}

function fmtRelativePast(ts) {
  if (!ts) return "—";
  const ms = Date.now() - ts * 1000;
  if (ms < 0) return "just now";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// Mirrors PUSH_TRIGGERS in backend/services/alert_dispatcher.py.
// fib_watching and fib_invalidated were promoted to push on 2026-05-19.
const TRIGGER_META = {
  fib_entry:       { badge: "🎯", label: "Fib ENTRY",   push: true  },
  fib_watching:    { badge: "👀", label: "In zone",     push: true  },
  fib_invalidated: { badge: "📉", label: "Invalidated", push: true  },
  bleed_watch:     { badge: "🟡", label: "Watch",       push: false },
  bleed_bleeding:  { badge: "🩸", label: "Bleeding",    push: true  },
  bleed_critical:  { badge: "🔴", label: "Critical",    push: true  },
  bleed_dead:      { badge: "⚫", label: "Dead",        push: true  },
};

function triggerMeta(t) {
  return TRIGGER_META[t] || { badge: "·", label: t, push: false };
}

function WatchlistRow({ token, onRemove, onToggleFib, onMute, onSnooze }) {
  const meta = tierMeta(token.tier);
  const TierIcon = meta.icon;
  const liq = token.liquidity || {};
  const dexUrl = token.pair_address
    ? `https://dexscreener.com/solana/${token.pair_address}`
    : `https://dexscreener.com/solana/${token.mint}`;
  const alerts = token.alerts || {};
  const muted = !!alerts.muted;
  const snoozeRemaining = fmtRelativeFuture(alerts.snoozed_until);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.25 }}
      className="grid grid-cols-12 gap-3 items-center px-3 py-3 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--bg-elev-2))]"
      data-testid={`watchlist-row-${token.mint}`}
    >
      {/* Token identity */}
      <div className="col-span-12 sm:col-span-3 flex items-center gap-2 min-w-0">
        <Badge variant={meta.variant} className="shrink-0">
          <TierIcon size={11} className="mr-1" />
          {meta.label}
        </Badge>
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">
            {token.symbol || "?"}{" "}
            <span className="text-[hsl(var(--muted))] font-normal">
              {token.name ? `· ${token.name}` : ""}
            </span>
          </div>
          <div className="text-[11px] text-[hsl(var(--muted))] font-mono">
            {shortMint(token.mint)}
          </div>
        </div>
      </div>

      {/* Liquidity numbers */}
      <div className="col-span-6 sm:col-span-3 text-sm">
        <div className="font-mono">{fmtUsd(liq.current_usd)}</div>
        <div className="text-[11px] text-[hsl(var(--muted))]">
          base {fmtUsd(liq.baseline_usd)} · peak {fmtUsd(liq.peak_usd)}
        </div>
      </div>

      {/* Deltas */}
      <div className="col-span-6 sm:col-span-2 text-xs space-y-0.5">
        <div>
          <span className="text-[hsl(var(--muted))]">tick </span>
          <DeltaText pct={liq.delta_from_prev_pct} />
        </div>
        <div>
          <span className="text-[hsl(var(--muted))]">base </span>
          <DeltaText pct={liq.delta_from_baseline_pct} />
        </div>
        <div>
          <span className="text-[hsl(var(--muted))]">peak </span>
          <DeltaText pct={liq.delta_from_peak_pct} />
        </div>
      </div>

      {/* Sparkline + Fib ladder side-by-side */}
      <div className="col-span-8 sm:col-span-2 flex items-start gap-1">
        <div>
          <Sparkline history={token.history} tier={token.tier} />
          <div className="text-[10px] text-[hsl(var(--muted))] mt-0.5">
            age {fmtAge(token.age_seconds)}
          </div>
        </div>
        {token.track_fib ? (
          <div className="shrink-0">
            <FibLadder fib={token.fib} />
          </div>
        ) : null}
      </div>

      {/* Actions */}
      <div className="col-span-4 sm:col-span-2 flex flex-col items-end gap-1.5">
        <div className="flex items-center gap-1.5">
          <a
            href={dexUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center h-7 w-7 rounded-md border border-[hsl(var(--border))] hover:bg-[hsl(var(--bg-elev-3))]"
            title="Open on DexScreener"
            aria-label="Open on DexScreener"
          >
            <ExternalLink size={13} />
          </a>
          <button
            type="button"
            onClick={() => onToggleFib(token.mint, !token.track_fib)}
            className={`inline-flex items-center justify-center h-7 w-7 rounded-md border ${
              token.track_fib
                ? "border-[hsl(var(--success)/0.5)] text-[hsl(var(--success))] bg-[hsl(var(--success)/0.08)]"
                : "border-[hsl(var(--border))] hover:bg-[hsl(var(--bg-elev-3))]"
            }`}
            title={token.track_fib ? "Stop tracking Fib entry signals" : "Track Fib entry signals"}
            aria-label="Toggle Fib tracking"
            data-testid={`watchlist-fib-toggle-${token.mint}`}
          >
            <Target size={13} />
          </button>
          <button
            type="button"
            onClick={() => onSnooze(token.mint, 1)}
            className={`inline-flex items-center justify-center h-7 w-7 rounded-md border ${
              snoozeRemaining
                ? "border-[hsl(var(--warning)/0.5)] text-[hsl(var(--warning))] bg-[hsl(var(--warning)/0.08)]"
                : "border-[hsl(var(--border))] hover:bg-[hsl(var(--bg-elev-3))]"
            }`}
            title={
              snoozeRemaining
                ? `Snoozed (${snoozeRemaining} left) — click to extend 1h`
                : "Snooze Telegram alerts for 1 hour"
            }
            aria-label="Snooze Telegram alerts"
            data-testid={`watchlist-snooze-${token.mint}`}
          >
            <Clock size={13} />
          </button>
          <button
            type="button"
            onClick={() => onMute(token.mint, !muted)}
            className={`inline-flex items-center justify-center h-7 w-7 rounded-md border ${
              muted
                ? "border-[hsl(var(--danger)/0.5)] text-[hsl(var(--danger))] bg-[hsl(var(--danger)/0.08)]"
                : "border-[hsl(var(--border))] hover:bg-[hsl(var(--bg-elev-3))]"
            }`}
            title={muted ? "Unmute Telegram alerts for this mint" : "Mute Telegram alerts for this mint"}
            aria-label="Toggle mute"
            data-testid={`watchlist-mute-${token.mint}`}
          >
            {muted ? <VolumeX size={13} /> : <Volume2 size={13} />}
          </button>
          <button
            type="button"
            onClick={() => onRemove(token.mint)}
            className="inline-flex items-center justify-center h-7 w-7 rounded-md border border-[hsl(var(--border))] hover:bg-[hsl(var(--danger)/0.1)] hover:text-[hsl(var(--danger))] hover:border-[hsl(var(--danger)/0.4)]"
            title="Stop watching"
            aria-label="Stop watching"
            data-testid={`watchlist-remove-${token.mint}`}
          >
            <Trash2 size={13} />
          </button>
        </div>
        {token.track_fib && token.fib ? (
          <FibSignalBadge fib={token.fib} />
        ) : null}
        {muted ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border border-[hsl(var(--danger)/0.4)] bg-[hsl(var(--danger)/0.1)] text-[hsl(var(--danger))]">
            <VolumeX size={10} /> Muted
          </span>
        ) : snoozeRemaining ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border border-[hsl(var(--warning)/0.4)] bg-[hsl(var(--warning)/0.1)] text-[hsl(var(--warning))]">
            <Clock size={10} /> Snoozed {snoozeRemaining}
          </span>
        ) : null}
      </div>

      {/* Reason (full width) */}
      {token.reason ? (
        <div className="col-span-12 text-[11px] text-[hsl(var(--muted))] -mt-1">
          {token.reason}
        </div>
      ) : null}

      {/* Fib reason / metrics (full width) — only when tracking + has state */}
      {token.track_fib && token.fib && token.fib.reason ? (
        <div className="col-span-12 text-[11px] text-[hsl(var(--muted))] -mt-1">
          <span className="font-mono">Fib:</span> {token.fib.reason}
          {token.fib.swing_low != null && token.fib.swing_high != null ? (
            <span className="ml-2 font-mono">
              · swing {fmtPrice(token.fib.swing_low)} → {fmtPrice(token.fib.swing_high)}
            </span>
          ) : null}
        </div>
      ) : null}
    </motion.div>
  );
}

function DeltaText({ pct }) {
  if (pct == null) return <span className="text-[hsl(var(--muted))]">—</span>;
  const cls =
    pct <= -10
      ? "text-[hsl(var(--danger))]"
      : pct < 0
      ? "text-[hsl(var(--warning))]"
      : "text-[hsl(var(--success))]";
  return <span className={`font-mono ${cls}`}>{fmtPct(pct)}</span>;
}

/**
 * Request OS notification permission lazily. Returns the resulting permission
 * string, or 'unsupported' if the browser has no Notification API.
 */
async function requestNotifPermission() {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return "unsupported";
  }
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  try {
    return await Notification.requestPermission();
  } catch {
    return "denied";
  }
}

/**
 * Fire a browser notification only on critical/dead transitions.
 * Deduped via `firedKeysRef`: the same mint+tier-change pair only ever
 * notifies once.
 */
function maybeNotify(token, prevTier, firedKeysRef) {
  if (typeof window === "undefined" || !("Notification" in window)) return;
  if (Notification.permission !== "granted") return;
  const tier = token.tier;
  if (tier !== "critical" && tier !== "dead") return;
  if (prevTier === tier) return; // only on transitions into this tier
  const key = `${token.mint}:${tier}:${Math.floor(
    (token.last_tier_change_at || 0) * 1000
  )}`;
  if (firedKeysRef.current.has(key)) return;
  firedKeysRef.current.add(key);

  const meta = tierMeta(tier);
  const title = `${meta.badge} ${token.symbol || shortMint(token.mint)} — ${meta.label}`;
  const body =
    token.reason ||
    `Liquidity now ${fmtUsd(
      (token.liquidity || {}).current_usd
    )} · ${fmtPct((token.liquidity || {}).delta_from_baseline_pct)} from baseline`;
  try {
    const n = new Notification(title, {
      body,
      tag: `liquidity-bleed:${token.mint}`,
      renotify: true,
    });
    // Clicking the notification focuses the tab.
    n.onclick = () => {
      window.focus();
      n.close();
    };
  } catch {
    // Browsers occasionally throw if too many notifs queued. Silent ignore.
  }
}

/**
 * Fire a browser notification on Fibonacci ENTRY transitions.
 * Deduped via `last_entry_signal_ts` so each entry within a swing cycle
 * notifies exactly once.
 */
function maybeNotifyFibEntry(token, firedFibRef) {
  if (typeof window === "undefined" || !("Notification" in window)) return;
  if (Notification.permission !== "granted") return;
  if (!token.track_fib) return;
  const fib = token.fib;
  if (!fib || fib.signal !== "entry") return;
  const ts = fib.last_entry_signal_ts;
  if (!ts) return;
  const key = `${token.mint}:${Math.floor(ts * 1000)}`;
  if (firedFibRef.current.has(key)) return;
  firedFibRef.current.add(key);

  const title = `🎯 ${token.symbol || shortMint(token.mint)} — Fib ENTRY`;
  const body =
    fib.reason ||
    `Pullback held the 50–61.8 zone with declining sell pressure.`;
  try {
    const n = new Notification(title, {
      body,
      tag: `fib-entry:${token.mint}:${ts}`,
      renotify: true,
    });
    n.onclick = () => {
      window.focus();
      n.close();
    };
  } catch {
    // Silent ignore on notification API failures.
  }
}

// ---------------------------------------------------------------------------
// AlertLogDrawer — slides in from the right; lists the last 50 Telegram alerts
// from /api/alerts/log. Used for journaling decisions ("did I take the fib_entry
// at 19:42? what was the price?").
// ---------------------------------------------------------------------------

function AlertLogDrawer({ open, onClose }) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const data = await fetchAlertLog(50, null);
      setRows(Array.isArray(data?.alerts) ? data.alerts : []);
    } catch (e) {
      setErr(String(e?.response?.data?.detail || e?.message || "failed"));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (open) load();
  }, [open, load]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      role="dialog"
      aria-modal="true"
      aria-label="Watchlist alert log"
      data-testid="alert-log-drawer"
    >
      {/* backdrop */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />
      <motion.aside
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "tween", duration: 0.2 }}
        className="relative w-full sm:w-[480px] h-full bg-[hsl(var(--bg-elev-1))] border-l border-[hsl(var(--border))] shadow-xl flex flex-col"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[hsl(var(--border))]">
          <div className="flex items-center gap-2">
            <ScrollText size={15} className="text-[hsl(var(--muted))]" />
            <div>
              <h3 className="text-sm font-semibold">Alert log</h3>
              <p className="text-[11px] text-[hsl(var(--muted))]">
                Last 50 Telegram alerts — keep IDs for your journal.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={load}
              disabled={loading}
              data-testid="alert-log-refresh"
            >
              {loading ? <Loader2 size={12} className="animate-spin" /> : "Refresh"}
            </Button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close alert log"
              className="inline-flex items-center justify-center h-7 w-7 rounded-md border border-[hsl(var(--border))] hover:bg-[hsl(var(--bg-elev-3))]"
              data-testid="alert-log-close"
            >
              <X size={14} />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1.5">
          {err ? (
            <div className="text-xs text-[hsl(var(--danger))] py-4 text-center">{err}</div>
          ) : loading && rows.length === 0 ? (
            <div className="text-xs text-[hsl(var(--muted))] py-6 text-center">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-xs text-[hsl(var(--muted))] py-6 text-center border border-dashed border-[hsl(var(--border))] rounded-md">
              No alerts yet. The first Fib entry or Bleed transition will land here.
            </div>
          ) : (
            rows.map((row) => {
              const meta = triggerMeta(row.trigger_type);
              const ok = row.sent_ok === 1 || row.sent_ok === true;
              const aid = row.id;
              return (
                <div
                  key={aid}
                  className="px-2.5 py-2 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--bg-elev-2))]"
                  data-testid={`alert-log-row-${aid}`}
                >
                  <div className="flex items-center justify-between gap-2 text-xs">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span>{meta.badge}</span>
                      <span className="font-medium truncate">
                        {row.ticker || shortMint(row.mint)}
                      </span>
                      <span className="text-[hsl(var(--muted))]">· {meta.label}</span>
                      {meta.push ? (
                        <Badge variant="danger" className="h-4 px-1 text-[9px]">PUSH</Badge>
                      ) : (
                        <Badge variant="muted" className="h-4 px-1 text-[9px]">silent</Badge>
                      )}
                    </div>
                    <span
                      className={`text-[10px] font-mono ${
                        ok ? "text-[hsl(var(--success))]" : "text-[hsl(var(--danger))]"
                      }`}
                      title={row.error || (ok ? "delivered" : "failed")}
                    >
                      {ok ? "✓ sent" : "✗ failed"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-1 text-[10px] text-[hsl(var(--muted))] font-mono">
                    <span title={aid}>id {aid}</span>
                    <span>{fmtRelativePast(row.ts)}</span>
                  </div>
                  {row.error && !ok ? (
                    <div className="text-[10px] text-[hsl(var(--danger))] mt-1 truncate" title={row.error}>
                      {row.error}
                    </div>
                  ) : null}
                </div>
              );
            })
          )}
        </div>
      </motion.aside>
    </div>
  );
}

export function LiquidityBleedDashboard() {
  const [tokens, setTokens] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [adding, setAdding] = React.useState(false);
  const [mintInput, setMintInput] = React.useState("");
  const [trackFibInput, setTrackFibInput] = React.useState(false);
  const [notifPermission, setNotifPermission] = React.useState(
    typeof window !== "undefined" && "Notification" in window
      ? Notification.permission
      : "unsupported"
  );
  const [logOpen, setLogOpen] = React.useState(false);
  const prevTiersRef = React.useRef(new Map()); // mint -> last tier seen
  const firedKeysRef = React.useRef(new Set());
  const firedFibRef = React.useRef(new Set()); // dedupe Fib entry notifs
  const visibleRef = React.useRef(true);

  // Track tab visibility so we only poll when the user can actually see it.
  React.useEffect(() => {
    const onVis = () => {
      visibleRef.current = !document.hidden;
    };
    document.addEventListener("visibilitychange", onVis);
    onVis();
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const refresh = React.useCallback(async () => {
    try {
      const data = await fetchWatchlist();
      const list = Array.isArray(data?.tokens) ? data.tokens : [];
      // Detect tier transitions + Fib entry signals for browser notifications.
      for (const tok of list) {
        const prev = prevTiersRef.current.get(tok.mint);
        maybeNotify(tok, prev, firedKeysRef);
        maybeNotifyFibEntry(tok, firedFibRef);
        prevTiersRef.current.set(tok.mint, tok.tier);
      }
      // Drop tracking for tokens that were removed.
      const stillPresent = new Set(list.map((t) => t.mint));
      for (const mint of Array.from(prevTiersRef.current.keys())) {
        if (!stillPresent.has(mint)) prevTiersRef.current.delete(mint);
      }
      // Sort: live Fib entry signals first, then tier severity desc, then
      // drawdown desc.
      list.sort((a, b) => {
        const fa = fibSignalMeta(a.fib?.signal).rank * (a.track_fib ? 1 : 0);
        const fb = fibSignalMeta(b.fib?.signal).rank * (b.track_fib ? 1 : 0);
        if (fb !== fa) return fb - fa;
        const sa = tierMeta(a.tier).rank;
        const sb = tierMeta(b.tier).rank;
        if (sb !== sa) return sb - sa;
        const da = (a.liquidity || {}).delta_from_baseline_pct ?? 0;
        const db = (b.liquidity || {}).delta_from_baseline_pct ?? 0;
        return da - db;
      });
      setTokens(list);
    } catch (err) {
      // Swallow polling errors silently — toast on first failure only.
      console.warn("[watchlist] refresh failed", err);
    }
  }, []);

  // Initial load + polling loop.
  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      await refresh();
      if (!cancelled) setLoading(false);
    })();
    const id = setInterval(() => {
      if (visibleRef.current) refresh();
    }, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [refresh]);

  const handleAdd = async (e) => {
    e?.preventDefault?.();
    const mint = mintInput.trim();
    if (!mint) return;
    if (!SOL_MINT_RE.test(mint)) {
      toast.error("Invalid mint", {
        description: "Paste a Solana mint address (Base58, 32–44 chars).",
      });
      return;
    }
    setAdding(true);
    try {
      const { token } = await addToWatchlist(mint, trackFibInput);
      toast.success(`Watching ${token?.symbol || shortMint(mint)}`, {
        description: `Baseline ${fmtUsd(token?.liquidity?.baseline_usd)} liquidity captured${
          trackFibInput ? " · Fib entry signals on" : ""
        }.`,
      });
      setMintInput("");
      setTrackFibInput(false);
      await refresh();
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to add to watchlist.";
      toast.error("Could not add", { description: String(msg) });
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (mint) => {
    try {
      await removeFromWatchlist(mint);
      prevTiersRef.current.delete(mint);
      toast.success("Stopped watching");
      await refresh();
    } catch (err) {
      toast.error("Remove failed", {
        description: String(err?.response?.data?.detail || err?.message || ""),
      });
    }
  };

  const handleSnooze = async (mint, hours) => {
    try {
      await snoozeAlerts(mint, hours);
      toast.success(`Snoozed ${hours}h`, {
        description: "Telegram alerts for this mint paused. UI badges keep updating.",
      });
      await refresh();
    } catch (err) {
      toast.error("Snooze failed", {
        description: String(err?.response?.data?.detail || err?.message || ""),
      });
    }
  };

  const handleMute = async (mint, nextMuted) => {
    try {
      await muteAlerts(mint, nextMuted);
      toast.success(nextMuted ? "Muted" : "Unmuted", {
        description: nextMuted
          ? "Telegram alerts off for this mint. UI badges keep updating."
          : "Telegram alerts re-enabled.",
      });
      await refresh();
    } catch (err) {
      toast.error(nextMuted ? "Mute failed" : "Unmute failed", {
        description: String(err?.response?.data?.detail || err?.message || ""),
      });
    }
  };

  const handleToggleFib = async (mint, nextValue) => {
    try {
      await patchWatchlist(mint, nextValue);
      toast.success(
        nextValue ? "Fib entry signals on" : "Fib entry signals off",
        nextValue
          ? { description: "Browser alerts on entry transitions." }
          : undefined
      );
      await refresh();
    } catch (err) {
      toast.error("Toggle failed", {
        description: String(err?.response?.data?.detail || err?.message || ""),
      });
    }
  };

  const handleNotifToggle = async () => {
    const result = await requestNotifPermission();
    setNotifPermission(result);
    if (result === "granted") {
      toast.success("Browser notifications enabled", {
        description: "You'll be pinged on Critical and Dead transitions.",
      });
    } else if (result === "denied") {
      toast.error("Notifications blocked", {
        description: "Allow notifications in your browser site settings.",
      });
    } else if (result === "unsupported") {
      toast.error("Not supported", {
        description: "This browser doesn't support web notifications.",
      });
    }
  };

  const tierCounts = React.useMemo(() => {
    const c = { critical: 0, dead: 0, bleeding: 0, watch: 0, safe: 0 };
    for (const t of tokens) c[t.tier] = (c[t.tier] || 0) + 1;
    return c;
  }, [tokens]);

  return (
    <Card className="p-4 sm:p-5" data-testid="liquidity-bleed-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-[hsl(var(--danger)/0.12)] border border-[hsl(var(--danger)/0.35)]">
            <Droplet size={14} className="text-[hsl(var(--danger))]" />
          </span>
          <div>
            <h2 className="text-sm font-semibold">Liquidity Bleed Monitor</h2>
            <p className="text-[11px] text-[hsl(var(--muted))]">
              Liquidity drawdown + Fib entry signals on watched pairs. Telegram
              alerts go to the dedicated Watchlist Signals channel.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {tokens.length > 0 ? (
            <div className="flex gap-1.5 flex-wrap">
              {tierCounts.critical ? (
                <Badge variant="danger">🔴 {tierCounts.critical}</Badge>
              ) : null}
              {tierCounts.dead ? (
                <Badge variant="danger">⚫ {tierCounts.dead}</Badge>
              ) : null}
              {tierCounts.bleeding ? (
                <Badge variant="warning">🟠 {tierCounts.bleeding}</Badge>
              ) : null}
              {tierCounts.watch ? (
                <Badge variant="warning">🟡 {tierCounts.watch}</Badge>
              ) : null}
              {tierCounts.safe ? (
                <Badge variant="success">🟢 {tierCounts.safe}</Badge>
              ) : null}
            </div>
          ) : null}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setLogOpen(true)}
            className="gap-1.5"
            data-testid="watchlist-open-log"
          >
            <ScrollText size={13} /> Alert log
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleNotifToggle}
            className="gap-1.5"
            data-testid="watchlist-notif-toggle"
          >
            {notifPermission === "granted" ? (
              <>
                <BellRing size={13} /> Notifications on
              </>
            ) : (
              <>
                <BellOff size={13} /> Enable alerts
              </>
            )}
          </Button>
        </div>
      </div>

      <CardContent className="p-0">
        <form
          onSubmit={handleAdd}
          className="flex flex-col gap-2 mb-4"
        >
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              value={mintInput}
              onChange={(e) => setMintInput(e.target.value)}
              placeholder="Paste a Solana mint address to watch…"
              className="font-mono text-xs"
              data-testid="watchlist-mint-input"
              disabled={adding}
            />
            <Button
              type="submit"
              disabled={adding || !mintInput.trim()}
              className="gap-1.5"
              data-testid="watchlist-add-button"
            >
              {adding ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <Plus size={13} />
              )}
              Watch
            </Button>
          </div>
          <label className="inline-flex items-center gap-2 text-[11px] text-[hsl(var(--muted))] cursor-pointer select-none">
            <input
              type="checkbox"
              checked={trackFibInput}
              onChange={(e) => setTrackFibInput(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-[hsl(var(--border))] accent-[hsl(var(--success))]"
              data-testid="watchlist-track-fib-checkbox"
              disabled={adding}
            />
            <Target size={11} className="text-[hsl(var(--success))]" />
            <span>
              Also track entry signals via Fibonacci retracement (browser alert
              on entry, no Telegram)
            </span>
          </label>
        </form>

        {loading && tokens.length === 0 ? (
          <div className="text-xs text-[hsl(var(--muted))] py-6 text-center">
            Loading watchlist…
          </div>
        ) : tokens.length === 0 ? (
          <div className="text-xs text-[hsl(var(--muted))] py-6 text-center border border-dashed border-[hsl(var(--border))] rounded-md">
            No tokens being watched. Paste a mint above to start monitoring its
            liquidity for sudden drops.
          </div>
        ) : (
          <div className="space-y-2">
            <AnimatePresence initial={false}>
              {tokens.map((t) => (
                <WatchlistRow
                  key={t.mint}
                  token={t}
                  onRemove={handleRemove}
                  onToggleFib={handleToggleFib}
                  onMute={handleMute}
                  onSnooze={handleSnooze}
                />
              ))}
            </AnimatePresence>
          </div>
        )}
      </CardContent>
      <AlertLogDrawer open={logOpen} onClose={() => setLogOpen(false)} />
    </Card>
  );
}

export default LiquidityBleedDashboard;
